/**
 * In-memory cache with pending request deduplication.
 */

export interface CacheEntry<T> {
  data: T;
  expiresAt: number;
  updatedAt: number;
  ttl: number;
}

export interface CacheOptions {
  ttl: number;
  staleWhileRevalidate?: boolean;
}

export interface CacheStats {
  hits: number;
  misses: number;
  pendingRequests: number;
  entries: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const pending = new Map<string, Promise<unknown>>();

const stats = {
  hits: 0,
  misses: 0,
};

function isExpired(entry: CacheEntry<unknown>): boolean {
  return Date.now() > entry.expiresAt;
}

export function getCacheStats(): CacheStats {
  return {
    hits: stats.hits,
    misses: stats.misses,
    pendingRequests: pending.size,
    entries: cache.size,
  };
}

export function clearCache(): void {
  cache.clear();
  pending.clear();
}

// Keys written through the client carry the audience they were fetched for
// as `<locale>|<currency>|<key>`. Callers invalidate by the bare key or
// prefix (`products:my-store`) and mean every audience's copy of it.
const AUDIENCE_PREFIX = /^[^|]*\|[^|]*\|/;

export function invalidateCache(prefixOrKey: string): void {
  for (const key of cache.keys()) {
    const bareKey = key.replace(AUDIENCE_PREFIX, '');
    if (
      key === prefixOrKey
      || key.startsWith(prefixOrKey)
      || bareKey === prefixOrKey
      || bareKey.startsWith(prefixOrKey)
    ) {
      cache.delete(key);
    }
  }
}

export function setCacheEntry<T>(key: string, data: T, ttl: number): void {
  const now = Date.now();
  cache.set(key, {
    data,
    ttl,
    updatedAt: now,
    expiresAt: now + ttl,
  });
}

export function getCacheEntry<T>(key: string): CacheEntry<T> | null {
  const entry = cache.get(key) as CacheEntry<T> | undefined;
  if (!entry) return null;
  return entry;
}

export async function getOrFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: CacheOptions,
  shouldCache: (value: T) => boolean = () => true
): Promise<T> {
  const entry = getCacheEntry<T>(key);

  if (entry && !isExpired(entry)) {
    stats.hits += 1;
    return entry.data;
  }

  if (entry && options.staleWhileRevalidate) {
    stats.hits += 1;
    if (!pending.has(key)) {
      const refreshPromise = (async () => {
        try {
          const data = await fetcher();
          if (shouldCache(data)) {
            setCacheEntry(key, data, options.ttl);
          }
          return data;
        } finally {
          pending.delete(key);
        }
      })();
      pending.set(key, refreshPromise as Promise<unknown>);
    }
    return entry.data;
  }

  if (pending.has(key)) {
    return pending.get(key) as Promise<T>;
  }

  stats.misses += 1;
  const promise = (async () => {
    try {
      const data = await fetcher();
      if (shouldCache(data)) {
        setCacheEntry(key, data, options.ttl);
      }
      return data;
    } finally {
      pending.delete(key);
    }
  })();

  pending.set(key, promise as Promise<unknown>);
  return promise;
}
